/**
 * Bond utility functions for building ARIA tree structure
 *
 * @package @alchemiesolns/svg-interactive
 */

import { MolecularWorkspace } from '@alchemiesolns/molecular-workspace';
import type { Bond } from '@alchemiesolns/molecular-workspace';
import { nameBondBetween, nameBondEndpoint } from '@alchemiesolns/feature-extractor';
import type { BondLeaf, BondEndpointLeaf } from './types';

/**
 * Find the bond connecting two atoms
 */
export function findBondBetween(
  workspace: MolecularWorkspace,
  atomId1: string,
  atomId2: string
): { bondId: string; bond: Bond } | null {
  for (const bond of workspace.getAllBonds()) {
    if (
      (bond.a1.id === atomId1 && bond.a2.id === atomId2) ||
      (bond.a2.id === atomId1 && bond.a1.id === atomId2)
    ) {
      return { bondId: bond.id, bond };
    }
  }
  return null;
}

/**
 * PIPH-74: Compute spatial direction from parent atom to other atom.
 * Used for stereo context bonds to help screen reader users mentally reconstruct
 * 3D orientation around potential stereocenters.
 *
 * Direction is based on comparing positions:
 * - x positive → right, x negative → left
 * - y positive → up, y negative → down
 * - Combine for diagonals: "up and to the right", "down and to the left"
 *
 * @returns Direction string or empty string if positions are identical
 */
export function computeDirection(
  parentX: number,
  parentY: number,
  otherX: number,
  otherY: number
): string {
  const dx = otherX - parentX;
  const dy = otherY - parentY;

  // Handle edge case where atoms are at same position
  if (dx === 0 && dy === 0) {
    return '';
  }

  // Determine horizontal and vertical components
  // Use a small threshold to avoid labeling near-horizontal/vertical as diagonal
  const threshold = 0.1;
  const horizontal = Math.abs(dx) > threshold ? (dx > 0 ? 'right' : 'left') : '';
  const vertical = Math.abs(dy) > threshold ? (dy > 0 ? 'up' : 'down') : '';

  // Combine components
  if (vertical && horizontal) {
    return `${vertical} and to the ${horizontal}`;
  }
  if (vertical) {
    return vertical;
  }
  if (horizontal) {
    return `to the ${horizontal}`;
  }
  return '';
}

/**
 * Creates a bond leaf for an atom (bond as child of atom)
 * The bond shows "single bond to {OtherAtom}" when navigated through parent atom
 * Also creates endpoint children for the bond
 *
 * PIPH-74: When parentHasStereoContext is true, the bond label includes direction
 * suffix (e.g., "single bond to Carbon, up and to the right")
 */
export function createBondLeafForAtom(
  bondId: string,
  parentAtomId: string,
  otherAtomId: string,
  parentAtomSymbol: string,
  otherAtomSymbol: string,
  order: number,
  bondType: 'l' | 'd' | 'w' | 's' | undefined,
  treeIdPrefix: string,
  bondsById: Map<string, BondLeaf>,
  endpointsById: Map<string, BondEndpointLeaf>,
  parentById: Map<string, string>,
  parentTreeId: string,
  parentAtomIndex?: number,
  totalOfParentElement?: number,
  otherAtomIndex?: number,
  totalOfOtherElement?: number,
  parentAtomX?: number,
  parentAtomY?: number,
  otherAtomX?: number,
  otherAtomY?: number,
  parentHasStereoContext?: boolean
): BondLeaf {
  // Create a unique tree ID for this bond instance (scoped to parent atom)
  const treeId = `${treeIdPrefix}:${bondId}`;

  // Create endpoint children - ID format: bondTreeId:atomId
  const endpoint1: BondEndpointLeaf = {
    type: 'bondendpoint',
    id: `${treeId}:${parentAtomId}`,
    bondId,
    atomId: parentAtomId,
    anchorAtomId: otherAtomId,  // Opposite atom is anchor
    label: nameBondEndpoint(parentAtomSymbol, parentAtomIndex, totalOfParentElement),
  };
  const endpoint2: BondEndpointLeaf = {
    type: 'bondendpoint',
    id: `${treeId}:${otherAtomId}`,
    bondId,
    atomId: otherAtomId,
    anchorAtomId: parentAtomId,  // Opposite atom is anchor
    label: nameBondEndpoint(otherAtomSymbol, otherAtomIndex, totalOfOtherElement),
  };

  // Register endpoints
  endpointsById.set(endpoint1.id, endpoint1);
  endpointsById.set(endpoint2.id, endpoint2);
  parentById.set(endpoint1.id, treeId);
  parentById.set(endpoint2.id, treeId);

  // PIPH-120: Generate context-independent bond label showing both endpoints.
  // This format works regardless of which atom the bond appears under in the tree.
  let label = nameBondBetween(
    parentAtomSymbol, parentAtomIndex, totalOfParentElement,
    otherAtomSymbol, otherAtomIndex, totalOfOtherElement,
    order, bondType
  );

  // PIPH-74: When parent atom has stereo context (wedge/dash bonds),
  // include direction for ALL bonds on that atom
  if (
    parentHasStereoContext &&
    parentAtomX !== undefined &&
    parentAtomY !== undefined &&
    otherAtomX !== undefined &&
    otherAtomY !== undefined
  ) {
    const direction = computeDirection(parentAtomX, parentAtomY, otherAtomX, otherAtomY);
    if (direction) {
      label = `${label}, ${direction}`;
    }
  }

  const bondLeaf: BondLeaf = {
    type: 'bond',
    id: treeId,
    bondId,
    atomIds: [parentAtomId, otherAtomId],
    order,
    bondType,
    parentAtomId,
    otherAtomId,
    label,
    endpointNodes: [endpoint1, endpoint2],
  };

  bondsById.set(treeId, bondLeaf);
  parentById.set(treeId, parentTreeId);

  return bondLeaf;
}
